import { Capacitor } from "@capacitor/core";
import { api } from "@/lib/api";
import { connectionType } from "@/lib/netStatus";

// Lot 713 — type de lien vu par l'appareil (wifi · cellular/4g · unknown/3g…) : Supervision regroupe les coupures
// par lieu (profil : ville, pays — côté serveur) et par type de réseau pour distinguer un opérateur d'une panne Yalola.
export const netTypeLabel = () => {
  const base = connectionType() || "unknown";
  const et = typeof navigator !== "undefined" ? navigator.connection?.effectiveType || "" : "";
  return (et && et !== "4g" ? `${base}/${et}` : (et ? `${base}/${et}` : base)).slice(0, 24);
};

// Coupure VUE PAR L'APP : l'écran « Reconnexion en cours… » (AppContext.refreshMe) note ici le début, les
// causes vues par l'appareil et chaque nouvel échec ; dès que le serveur répond à nouveau (même après un
// rechargement de page ou une réouverture de l'app : l'état vit dans localStorage), la fenêtre observée est
// envoyée à POST /client-incidents → « Historique des incidents » admin (« Coupure vue par les membres »).
// Un appareil hors ligne du début à la fin n'est PAS signalé (panne du réseau du membre, pas de Yalola).
const KEY = "yalola.reconnect.pending";
const OFFLINE = "appareil hors ligne";
const TAIL_MS = 10000;   // fin estimée = dernier échec + 10 s si l'app a été fermée entre-temps

const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; } };
const write = (p) => { try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* stockage indisponible */ } };
const clear = () => { try { localStorage.removeItem(KEY); } catch { /* rien */ } };
const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));

// Cause lisible d'un échec de /auth/me, telle que vue par l'appareil.
export function classifyNetError(e) {
  const st = e?.response?.status;
  if (st) return st >= 500 ? `HTTP ${st} (serveur / passerelle)` : `HTTP ${st}`;
  if (e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) return "délai dépassé (> 15 s)";
  if (typeof navigator !== "undefined" && navigator.onLine === false) return OFFLINE;
  return "serveur injoignable (erreur réseau)";
}

// `startedAt` = horodatage du PREMIER échec (3 essais de 15 s max, relances 1,5 s / 3 s, avant l'écran de reconnexion).
export function reconnectStarted(causes, startedAt = Date.now()) {
  if (read()) { reconnectFailedAgain(causes[causes.length - 1]); return; }   // déjà ouverte (rechargement pendant la coupure)
  write({ started_at: startedAt, last_failed_at: Date.now(), causes: uniq(causes), attempts: causes.length, path: (typeof location !== "undefined" && location.pathname) || "" });
}

export function reconnectFailedAgain(cause) {
  const p = read();
  if (!p) return;
  write({ ...p, last_failed_at: Date.now(), causes: uniq([...(p.causes || []), cause]).slice(0, 6), attempts: (p.attempts || 0) + 1 });
}

export function reconnectAbandoned() { clear(); }

export async function reconnectResolved() {
  const p = read();
  if (!p) return false;
  clear();
  const causes = p.causes || [];
  if (causes.length && causes.every((c) => c === OFFLINE)) return false;
  const now = Date.now();
  const resolved_at = Math.min(now, (p.last_failed_at || p.started_at) + TAIL_MS);
  if (resolved_at - p.started_at > 24 * 3600e3 || resolved_at < p.started_at) return false;
  let app_version = "";
  try {
    const { getAppVersion } = await import("@/lib/appVersion");
    const v = await getAppVersion();
    app_version = `${v.version} (${v.build})`;
  } catch { /* web */ }
  const platform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : "web";
  api.post("/client-incidents", { started_at: p.started_at, resolved_at, causes, attempts: p.attempts || 0, path: p.path || "", platform, app_version, net_type: netTypeLabel() }).catch(() => {});
  return true;
}
